(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => reducedMotion() ? 'auto' : 'smooth';
  const currentUrl = (href) => {
    const url = new URL(href, window.location.origin);
    const current = new URL(window.location.href);
    for (const key of ['preview_theme_id', 'view']) {
      if (current.searchParams.has(key) && url.pathname === current.pathname) {
        url.searchParams.set(key, current.searchParams.get(key));
      }
    }
    return url;
  };

  class GuideElement extends HTMLElement {
    connect() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.drawers = [...this.querySelectorAll('drawer-component')];
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.drawers?.forEach((drawer) => { if (drawer.open) drawer.hide(); });
      clearTimeout(this.refreshTimer);
      clearTimeout(this.toastTimer);
    }

    centerButton(button) {
      const row = button.parentElement;
      const offset = button.getBoundingClientRect().left - row.getBoundingClientRect().left;
      row.scrollBy({ left: offset - (row.clientWidth - button.offsetWidth) / 2, behavior: scrollBehavior() });
    }

    updateUrl(key, value, emptyValue = 'all') {
      const url = new URL(window.location.href);
      if (value === emptyValue) url.searchParams.delete(key);
      else url.searchParams.set(key, value);
      if (url.href !== window.location.href) window.history.pushState({}, '', url);
    }

    captureRefreshState() {
      const expandedTerms = [...this.querySelectorAll('details[data-terms-disclosure][id][open]')].map((terms) => terms.id);
      const focused = document.activeElement;
      if (!this.contains(focused)) return { expandedTerms };
      const card = focused.closest('[data-offer-key]');
      const attributes = ['id', 'data-offer-tab', 'data-sort-select', 'data-copy', 'data-member-signup', 'aria-controls', 'href'];
      const attribute = attributes.find((name) => focused.hasAttribute(name));
      return {
        expandedTerms,
        focus: {
          cardKey: card?.dataset.offerKey,
          selector: attribute
            ? `${focused.localName}[${attribute}="${CSS.escape(focused.getAttribute(attribute))}"]`
            : focused.localName === 'summary' ? 'summary' : null
        }
      };
    }

    restoreRefreshState({ expandedTerms, focus }) {
      expandedTerms.forEach((id) => {
        const terms = this.querySelector(`details[data-terms-disclosure][id="${CSS.escape(id)}"]`);
        if (terms) terms.open = true;
      });
      if (!focus) return;
      const scope = focus.cardKey
        ? this.querySelector(`[data-offer-key="${CSS.escape(focus.cardKey)}"]`)
        : this;
      const target = focus.selector && scope?.querySelector(focus.selector);
      const fallback = this.querySelector('[data-offer-tab][aria-pressed="true"]');
      const next = [target, fallback].find((element) => element && element.getClientRects().length && !element.closest('[hidden], [inert]'));
      if (next) next.focus({ preventScroll: true });
      else {
        this.setAttribute('tabindex', '-1');
        this.focus({ preventScroll: true });
      }
    }

    watchSchedule() {
      const deadline = Number(this.dataset.nextChange) * 1000;
      if (!deadline) return;
      const refresh = async () => {
        if (document.hidden || this.refreshing || !this.isConnected) return;
        if (this.drawers.some((drawer) => drawer.open || !this.contains(drawer))) {
          clearTimeout(this.refreshTimer);
          this.refreshTimer = setTimeout(refresh, 1000);
          return;
        }
        if (Date.now() < deadline) {
          clearTimeout(this.refreshTimer);
          this.refreshTimer = setTimeout(refresh, Math.min(deadline - Date.now() + 500, 2147483647));
          return;
        }
        this.refreshing = true;
        const signal = this.signal;
        const requestLocation = window.location.href;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, { signal, cache: 'no-store' });
          if (!response.ok) throw new Error('Section request failed');
          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const replacement = documentFragment.querySelector(this.localName);
          if (!replacement) throw new Error('Section missing');
          if (signal.aborted || signal !== this.signal || !this.isConnected) return;
          if (document.hidden || requestLocation !== window.location.href || this.drawers.some((drawer) => drawer.open || !this.contains(drawer))) {
            this.refreshTimer = setTimeout(refresh, 1000);
            return;
          }
          const state = this.captureRefreshState();
          this.replaceWith(replacement);
          replacement.restoreRefreshState(state);
        } catch (error) {
          if (error.name !== 'AbortError' && !signal.aborted && this.isConnected) {
            this.refreshTimer = setTimeout(refresh, 30000);
          }
        } finally {
          this.refreshing = false;
        }
      };
      this.refreshTimer = setTimeout(refresh, Math.max(1000, Math.min(deadline - Date.now() + 500, 2147483647)));
      document.addEventListener('visibilitychange', refresh, { signal: this.signal });
    }
  }

  if (!customElements.get('bg-scheduled')) {
    customElements.define('bg-scheduled', class extends GuideElement {
      connectedCallback() {
        this.connect();
        this.watchSchedule();
      }
    });
  }

  if (!customElements.get('bg-brands')) {
    customElements.define('bg-brands', class extends GuideElement {
      connectedCallback() {
        this.connect();
        this.tabs = [...this.querySelectorAll('[data-tab]')];
        this.tracks = [...this.querySelectorAll('[data-track]')];
        const activate = (name) => {
          this.tabs.forEach((tab) => {
            const active = tab.dataset.tab === name;
            tab.setAttribute('aria-pressed', String(active));
            tab.classList.toggle('active', active);
          });
          this.tracks.forEach((track) => {
            track.hidden = track.dataset.track !== name;
            if (!track.hidden) track.scrollLeft = 0;
          });
          this.updateProgress();
        };
        this.addEventListener('click', (event) => {
          const tab = event.target.closest('[data-tab]');
          if (tab) activate(tab.dataset.tab);
          const arrow = event.target.closest('[data-prev], [data-next]');
          const track = this.tracks.find((item) => !item.hidden);
          if (arrow && track) {
            const direction = arrow.hasAttribute('data-prev') ? -1 : 1;
            const factor = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
            track.scrollBy({ left: direction * factor * track.clientWidth * 0.9, behavior: scrollBehavior() });
          }
        }, { signal: this.signal });
        this.tracks.forEach((track) => track.addEventListener('scroll', () => this.updateProgress(), { passive: true, signal: this.signal }));
        this.railObserver = new ResizeObserver(() => this.updateProgress());
        this.tracks.forEach((track) => this.railObserver.observe(track));
        if (this.tabs.length) activate(this.tabs[0].dataset.tab);
      }

      updateProgress() {
        const track = this.tracks?.find((item) => !item.hidden);
        if (!track) return;
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        const scrolled = Math.min(maximum, Math.abs(track.scrollLeft));
        const ratio = Math.min(1, track.clientWidth / (track.scrollWidth || 1));
        const thumb = this.querySelector('[data-progress]');
        if (thumb) {
          thumb.style.width = `${ratio * 100}%`;
          thumb.style.marginInlineStart = `${maximum ? scrolled / maximum * (1 - ratio) * 100 : 0}%`;
        }
        this.querySelector('[data-prev]')?.toggleAttribute('disabled', scrolled < 1);
        this.querySelector('[data-next]')?.toggleAttribute('disabled', maximum - scrolled < 1);
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.railObserver?.disconnect();
      }
    });
  }

  if (!customElements.get('bg-themes')) {
    customElements.define('bg-themes', class extends GuideElement {
      connectedCallback() {
        this.connect();
        this.tabs = [...this.querySelectorAll('[data-theme-tab]')];
        this.cards = [...this.querySelectorAll('[data-theme-grid] > [data-category]')];
        this.addEventListener('click', (event) => {
          const tab = event.target.closest('[data-theme-tab]');
          if (!tab) return;
          this.activate(tab.dataset.themeTab);
          this.updateUrl('category', tab.dataset.themeTab);
          this.centerButton(tab);
        }, { signal: this.signal });
        const restore = () => this.activate(new URLSearchParams(window.location.search).get('category') || 'all');
        window.addEventListener('popstate', restore, { signal: this.signal });
        restore();
      }

      activate(category) {
        if (!this.tabs.some((tab) => tab.dataset.themeTab === category)) category = 'all';
        this.tabs.forEach((tab) => tab.setAttribute('aria-pressed', String(tab.dataset.themeTab === category)));
        this.cards.forEach((card) => { card.hidden = category !== 'all' && card.dataset.category !== category; });
        const count = this.querySelector('[data-themes-count]');
        if (count) count.textContent = this.dataset.countTemplate.replace('[count]', this.cards.filter((card) => !card.hidden).length);
      }
    });
  }

  if (!customElements.get('bg-featured')) {
    customElements.define('bg-featured', class extends GuideElement {
      connectedCallback() {
        this.connect();
        if (this.querySelectorAll('.swiper-slide').length < 2 || !window.FoxTheme?.Carousel) return;
        this.carousel = new FoxTheme.Carousel(this.querySelector('.swiper'), {
          slidesPerView: 1.07,
          spaceBetween: 12,
          breakpoints: { 768: { slidesPerView: 1, spaceBetween: 16 } },
          rewind: true,
          ...(reducedMotion() ? { speed: 0 } : {}),
          pagination: { el: this.querySelector('[data-featured-pagination]'), clickable: true },
          navigation: { prevEl: this.querySelector('[data-featured-prev]'), nextEl: this.querySelector('[data-featured-next]') }
        });
        this.carousel.init();
        this.paused = false;
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-featured-pause]');
          if (!button) return;
          this.paused = !this.paused;
          button.textContent = this.paused ? button.dataset.playLabel : button.dataset.pauseLabel;
        }, { signal: this.signal });
        if (this.dataset.autoplay === 'true') {
          this.autoplayTimer = setInterval(() => {
            if (!this.paused && !document.hidden && !reducedMotion() && window.innerWidth >= 768 && !this.matches(':hover') && !this.contains(document.activeElement)) this.carousel.slider.slideNext();
          }, 6000);
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        clearInterval(this.autoplayTimer);
        this.carousel?.slider?.destroy(true, true);
      }
    });
  }

  if (!customElements.get('bg-offers')) {
    customElements.define('bg-offers', class extends GuideElement {
      connectedCallback() {
        this.connect();
        this.tabs = [...this.querySelectorAll('[data-offer-tab]')];
        this.groups = [...this.querySelectorAll('[data-offer-section]')];
        this.defaultSort = this.dataset.defaultSort || 'featured';
        this.curatedOrder = new Map([...this.querySelectorAll('[data-offer-key]')].map((card, index) => [card, index]));
        this.sortDrawer = this.querySelector('[data-sort-drawer]');
        this.sortDrawer?.addEventListener('change', (event) => {
          const option = event.target.closest('[data-sort-option]');
          if (!option) return;
          event.stopPropagation();
          this.sort(option.dataset.sortOption);
          this.updateUrl('sort', option.dataset.sortOption, this.defaultSort);
        }, { signal: this.signal });
        this.addEventListener('click', (event) => {
          const tab = event.target.closest('[data-offer-tab]');
          const signup = event.target.closest('[data-member-signup]');
          if (signup) {
            window._klOnsite = window._klOnsite || [];
            window._klOnsite.push(['openForm', 'Ya6Mb7']);
          }
          if (tab) {
            this.activate(tab.dataset.offerTab);
            this.updateUrl('filter', tab.dataset.offerTab);
            this.centerButton(tab);
          }
          const copy = event.target.closest('button[data-copy]');
          if (copy) this.copy(copy);
        }, { signal: this.signal });
        this.addEventListener('change', (event) => {
          if (!event.target.matches('[data-sort-select]')) return;
          this.sort(event.target.value);
          this.updateUrl('sort', event.target.value, this.defaultSort);
        }, { signal: this.signal });
        const restore = () => {
          const params = new URLSearchParams(window.location.search);
          this.activate(params.get('filter') || 'all');
          this.sort(params.get('sort') || this.defaultSort);
        };
        window.addEventListener('popstate', restore, { signal: this.signal });
        restore();
        this.watchSchedule();
      }

      activate(name) {
        if (!this.tabs.some((tab) => tab.dataset.offerTab === name)) name = 'all';
        this.tabs.forEach((tab) => {
          const active = tab.dataset.offerTab === name;
          tab.setAttribute('aria-pressed', String(active));
          tab.classList.toggle('active', active);
        });
        this.querySelectorAll('[data-offer-key]').forEach((card) => {
          card.hidden = name !== 'all' && !(card.dataset.offerTabs || '').split(' ').includes(name);
        });
        this.groups.forEach((group) => {
          if (group.dataset.offerSection === 'member') {
            group.hidden = name !== 'all' && name !== 'member';
            return;
          }
          const count = [...group.querySelectorAll('[data-offer-key]')].filter((card) => !card.hidden).length;
          group.hidden = name === 'member' || count === 0;
          const label = group.querySelector('[data-offer-group-count]');
          if (label) label.textContent = count;
        });
        this.querySelectorAll('[data-offer-sort]').forEach((control) => { control.hidden = name === 'member'; });
        const empty = this.querySelector('[data-offer-empty]');
        if (empty) empty.hidden = name === 'member';
      }

      sort(value) {
        if (!['featured', 'newest', 'ending'].includes(value)) value = this.defaultSort;
        const select = this.querySelector('[data-sort-select]');
        if (select) select.value = value;
        this.querySelectorAll('[data-sort-option]').forEach((option) => { option.checked = option.dataset.sortOption === value; });
        this.sortDrawer?.querySelectorAll('[data-sort-option]').forEach((option) => { option.checked = option.dataset.sortOption === value; });
        this.querySelectorAll('[data-offer-grid]').forEach((grid) => {
          [...grid.children].sort((first, second) => value === 'featured'
            ? this.curatedOrder.get(first) - this.curatedOrder.get(second)
            : value === 'newest'
            ? Number(second.dataset.publishedTs || 0) - Number(first.dataset.publishedTs || 0)
            : (Number(first.dataset.endTs) || Number.MAX_SAFE_INTEGER) - (Number(second.dataset.endTs) || Number.MAX_SAFE_INTEGER)
          ).forEach((card) => grid.appendChild(card));
        });
      }

      copyFallback(code) {
        const field = document.createElement('textarea');
        const focusedElement = document.activeElement;
        field.value = code;
        field.readOnly = true;
        field.className = 'visually-hidden';
        field.setAttribute('aria-hidden', 'true');
        document.body.append(field);
        let copied = false;
        try {
          field.focus({ preventScroll: true });
          field.select();
          field.setSelectionRange(0, code.length);
          copied = document.execCommand('copy');
        } catch {
          copied = false;
        } finally {
          field.remove();
          focusedElement?.focus({ preventScroll: true });
        }
        return copied;
      }

      async copy(button) {
        let copied = false;
        try {
          await navigator.clipboard.writeText(button.dataset.copy);
          copied = true;
        } catch {
          copied = this.copyFallback(button.dataset.copy);
        }
        if (!this.isConnected) return;
        if (!copied) {
          const code = button.closest('[data-copy-row]')?.querySelector('.bg-code-row__label strong');
          const selection = window.getSelection();
          if (code && selection) {
            const range = document.createRange();
            range.selectNodeContents(code);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
        const message = copied ? button.dataset.toast || this.dataset.copiedLabel : this.dataset.copyError;
        const toast = this.querySelector('.bg-toast');
        toast.querySelector('[data-toast-text]').textContent = message;
        toast.hidden = false;
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => { toast.hidden = true; }, 3500);
      }
    });
  }

  if (!customElements.get('bg-collection')) {
    customElements.define('bg-collection', class extends GuideElement {
      connectedCallback() {
        this.connect();
        const previewView = new URLSearchParams(window.location.search).get('view');
        if (previewView) {
          this.querySelectorAll('form[is="facet-form"], .bg-fallback-sort').forEach((form) => {
            if (form.querySelector('input[name="view"]')) return;
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'view';
            input.value = previewView;
            form.appendChild(input);
          });
        }
        this.addEventListener('click', (event) => {
          const more = event.target.closest('[data-bg-load-more]');
          if (more) {
            event.preventDefault();
            this.loadMore(more);
            return;
          }
          const step = event.target.closest('.bg-routine-nav a');
          if (step) {
            event.preventDefault();
            const url = currentUrl(step.href);
            if (step.dataset.routineValue && !step.hasAttribute('aria-current')) url.searchParams.set('filter.p.m.custom.routine_step', step.dataset.routineValue);
            url.searchParams.delete('page');
            window.location.assign(url);
          }
        }, { signal: this.signal });
        window.addEventListener('popstate', () => window.location.reload(), { signal: this.signal });
      }

      async loadMore(button) {
        if (this.loading) return;
        this.loading = true;
        button.classList.add('btn--loading');
        button.setAttribute('aria-disabled', 'true');
        const errorMessage = this.querySelector('[data-load-error]');
        errorMessage.hidden = true;
        const requestLocation = window.location.href;
        const grid = this.querySelector('.products-list');
        try {
          const url = currentUrl(button.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, { signal: this.signal });
          if (!response.ok) throw new Error('Page request failed');
          const source = new DOMParser().parseFromString(await response.text(), 'text/html');
          const sourceGrid = source.querySelector('.products-list');
          if (!sourceGrid) throw new Error('Product grid missing');
          if (requestLocation !== window.location.href || grid !== this.querySelector('.products-list')) return;
          const firstNewProduct = sourceGrid.querySelector('.product-column');
          grid.append(...sourceGrid.children);
          FoxTheme.utils.appendCollectionSchema(this, source);
          const pagination = this.querySelector('.pagination');
          const next = source.querySelector('.pagination');
          if (next) pagination.replaceWith(next);
          else pagination.remove();
          const count = this.querySelector('[data-shown-count]');
          count.textContent = count.dataset.template.replace('[shown]', Number(count.dataset.offset || 0) + grid.querySelectorAll('.product-column').length);
          if (firstNewProduct) {
            firstNewProduct.setAttribute('tabindex', '-1');
            firstNewProduct.focus({ preventScroll: true });
          }
          document.dispatchEvent(new CustomEvent('collection:rerendered'));
        } catch (error) {
          if (error.name !== 'AbortError') {
            errorMessage.textContent = this.dataset.loadError;
            errorMessage.hidden = false;
          }
        } finally {
          this.loading = false;
          button.classList.remove('btn--loading');
          button.removeAttribute('aria-disabled');
        }
      }
    });
  }

  if (!customElements.get('bg-countdown')) {
    customElements.define('bg-countdown', class extends HTMLElement {
      connectedCallback() {
        const tick = () => {
          const remaining = Math.max(0, Number(this.dataset.endTs) - Math.floor(Date.now() / 1000));
          const output = this.querySelector('[data-time]');
          if (output) output.textContent = [Math.floor(remaining / 3600), Math.floor(remaining % 3600 / 60), remaining % 60].map((unit) => String(unit).padStart(2, '0')).join(':');
          if (!remaining) clearInterval(this.timer);
        };
        this.timer = setInterval(tick, 1000);
        tick();
      }
      disconnectedCallback() {
        clearInterval(this.timer);
      }
    });
  }
})();
